/**
 * Реестр маршрутов зеркала EN↔PT — единственный источник правды.
 *
 * Из него питаются: роутер (src/App.tsx), список prerender, карта сайта,
 * hreflang, переключатель языка и — через сборочный артефакт
 * /i18n/pt-routes.json — воркер Cloudflare.
 *
 * Ошибка версии v1 (BTQ-47, снята с прода 22.08.2026): PT-страницы жили
 * отдельным мини-сайтом со своей навигацией. Здесь португальская страница —
 * это та же страница сайта на другом языке: тот же пункт меню, тот же набор
 * фактов и цифр.
 *
 * ⚠️ Но зеркало — это СТРУКТУРА, а не форма страницы. Совпадать обязаны набор
 * маршрутов, навигация, переключатель, факты и числа. Внутренняя композиция
 * блоков у португальского двойника может и часто должна отличаться: SERP-замер
 * 17 запросов (BTQ-155) показал, что сервисная витрина не попадает в топ-10 ни
 * по одному коммерческому PT-запросу, а выигрывают каталог с числом и гео,
 * пошаговый гайд и подборка идей. Поэтому вид страницы задаётся ПО ЛОКАЛИ —
 * см. src/content/pages/types.ts.
 *
 * Слаги взяты из языка спроса (btq.pt/pt-core/pt-keyword-map.md §1 и
 * pt-keyword-map-ads.md), а не транслитерированы с английских.
 */

export type Locale = "en" | "pt";

/**
 * Состояние маршрута в зеркале. Три, а не два — потому что «объявлено» и
 * «существует» это разные вещи, и путать их дорого.
 *
 * mirrored — португальский двойник написан и живой. Только эти маршруты
 *            существуют: у них есть React-роут, статический HTML, запись в
 *            карте сайта, hreflang и строка в манифесте воркера.
 * planned  — пара объявлена (слаг решён, ключ занят), страницы ещё нет.
 *            Роут НЕ создаётся, адрес отдаёт 404 — и на проде, и на preview.
 *            Пустой или тонкий двойник хуже отсутствия: ровно за это версия
 *            v1 отправилась в откат.
 * excluded — двойника не будет, и это решение, а не забывчивость. Причина
 *            обязательна. Исключения — норма: часть английских страниц не
 *            имеет португальского спроса вовсе.
 */
export type RouteState = "mirrored" | "planned" | "excluded";


export const DEFAULT_LOCALE: Locale = "en";
export const PT_PREFIX = "/pt";
export const SITE_ORIGIN = "https://eventboutique.pt";
/** Сайт держит завершающий слэш в канонической форме адреса. */
export const TRAILING_SLASH = true;

export interface RouteEntry {
  /** Стабильный ключ страницы: связывает EN-маршрут, PT-маршрут, компонент и контент. */
  key: string;
  /**
   * Канонический английский путь — ровно как в карте сайта.
   *
   * null у PT-only страниц: их в английской версии нет и не будет. Такие
   * страницы ДОПОЛНЯЮТ зеркало, а не заменяют его — у них нет пары hreflang,
   * а переключатель на них не рисуется.
   */
  en: string | null;
  /** Португальский двойник в канонической форме. null у исключений. */
  pt: string | null;
  state: RouteState;
  /** Обязательна при state: "excluded". */
  reason?: string;
  /**
   * Куда вести ПОРТУГАЛЬСКОГО посетителя, пока своей страницы нет.
   *
   * Без этого поля пункт меню уводил его на АНГЛИЙСКИЙ адрес: `localise`
   * возвращала `entry.en` с флагом `fellBack`, а шапка рисовала обычную ссылку.
   * На португальской стороне так выпадали весь список «Regiões» (все одиннадцать
   * гео исключены из зеркала решением 23.08), тринадцать площадок, журнал и
   * «Produção de eventos» — то есть почти всё меню выбрасывало из португальской
   * версии в английскую. Это зеркальное повторение провала v1, где посетитель
   * проваливался из сайта в огрызок.
   *
   * Значение — ключ ЖИВОЙ португальской страницы, которая по решению покрывает
   * этот интент. Гео и площадки ведут в каталог quintas: спецификация
   * pt-specs-v2/01 прямо отдаёт гео-разрезы каталогу, а не отдельным страницам.
   *
   * Пусто — вести некуда, и тогда пункт меню на португальской стороне НЕ
   * рисуется вовсе. Это честнее ссылки, которая уводит с сайта.
   */
  ptFallback?: string;
  /**
   * Страница существует ТОЛЬКО по-английски, и это правильно.
   *
   * Наём и работа с поставщиками ведутся по-английски — переводить их значило бы
   * обещать португальский процесс, которого нет. Поэтому пункт остаётся в меню
   * и в футере на обеих сторонах, ведёт на английский адрес и помечается
   * `hrefLang="en"`, чтобы и посетитель, и поисковик видели смену языка.
   *
   * Это ЕДИНСТВЕННЫЙ случай, когда ссылка с португальской страницы законно
   * уходит на английскую. Везде остальное — либо своя страница, либо замена,
   * либо ничего.
   */
  ptKeepEnglish?: boolean;
  /**
   * Страницу отдаёт маршрут с параметром (/blog/:slug, /venues/:slug), а не
   * собственный компонент. Слаг для португальской стороны решён, но
   * маршрутизация по нему требует своего слоя соответствий slug EN↔PT — он
   * строится в волне журнала. До тех пор такая пара не может стать mirrored.
   */
  dynamic?: boolean;
}

export const ROUTES: RouteEntry[] = [
  // ⚠️ ТОЛЬКО ДЛЯ ВЕТКИ btq-47-a2-home. В main состояние обязано быть "planned":
  // карта v2, шаг A3 — «Состояние всех новых пар — planned, в проде они
  // по-прежнему отдают 404. Клон живёт и проверяется на preview».
  // Английский текст под /pt/ на боевом домене хуже, чем 404.
  { key: "home", en: "/", pt: "/pt/", state: "mirrored" },
  { key: "about", en: "/about-us/", pt: "/pt/sobre-nos/", state: "mirrored" },
  { key: "blog", en: "/blog/", pt: "/pt/blog/", state: "mirrored" },
  { key: "blog-beach", en: "/blog/romantic-beach-locations/", pt: "/pt/blog/casamento-na-praia-em-portugal/", state: "mirrored" },
  { key: "blog-palaces", en: "/blog/top-palaces-portugal/", pt: "/pt/blog/palacios-para-casamentos-em-portugal/", state: "mirrored" },
  { key: "careers", en: "/careers/", pt: null, state: "excluded", ptKeepEnglish: true,
    reason: "Наём идёт по-английски, португальского спроса на вакансии у нас нет" },
  { key: "contact", en: "/contact/", pt: "/pt/contactos/", state: "mirrored" },
  { key: "faq", en: "/faq/", pt: "/pt/perguntas-frequentes/", state: "mirrored" },
  { key: "legal", en: "/legal-requirements/", pt: "/pt/casar-em-portugal-documentos/", state: "mirrored" },
  // ── Регионы — пары решением владельца 27.08: «регионы и площадки — все
  // клонировать и переводить на португальский». Отменяет исключение 23.08
  // («гео живут внутри каталога quintas»). Слаги взяты из бывших PT-only
  // записей pt-casamentos-* (язык спроса, GSC-обоснование в истории гита);
  // сами PT-only записи удалены, чтобы не дублировать адреса.
  { key: "locations", en: "/locations/", pt: null, state: "excluded", ptFallback: "venues",
    reason: "Хаб ждёт слага: /pt/casamentos/ исключён 23.08 как каннибализирующий каталог quintas, а слаг хаба регионов не исследован — вопрос у владельца (27.08). Десять гео при этом — полноценные пары" },
  { key: "location-alentejo", en: "/locations/alentejo/", pt: "/pt/casamentos-alentejo/", state: "mirrored" },
  { key: "location-algarve", en: "/locations/algarve/", pt: "/pt/casamentos-algarve/", state: "mirrored" },
  { key: "location-cascais", en: "/locations/cascais/", pt: "/pt/casamentos-cascais/", state: "mirrored" },
  { key: "location-comporta", en: "/locations/comporta/", pt: "/pt/casamentos-comporta/", state: "mirrored" },
  { key: "location-douro-valley", en: "/locations/douro-valley/", pt: "/pt/casamentos-douro/", state: "mirrored" },
  { key: "location-hidden-gems", en: "/locations/hidden-gems/", pt: "/pt/casamentos-lugares-secretos/", state: "mirrored" },
  { key: "location-lisbon", en: "/locations/lisbon/", pt: "/pt/casamentos-lisboa/", state: "mirrored" },
  { key: "location-madeira", en: "/locations/madeira/", pt: "/pt/casamentos-madeira/", state: "mirrored" },
  { key: "location-porto", en: "/locations/porto/", pt: "/pt/casamentos-porto/", state: "mirrored" },
  { key: "location-sintra", en: "/locations/sintra/", pt: "/pt/casamentos-sintra/", state: "mirrored" },
  { key: "partners", en: "/partners/", pt: null, state: "excluded", ptKeepEnglish: true,
    reason: "Работа с поставщиками ведётся по-английски" },
  { key: "portfolio", en: "/portfolio/", pt: "/pt/portfolio/", state: "mirrored" },
  { key: "privacy", en: "/privacy-policy/", pt: "/pt/politica-de-privacidade/", state: "mirrored" },
  { key: "services", en: "/services/", pt: "/pt/servicos/", state: "mirrored" },
  { key: "service-creative-direction", en: "/services/creative-direction/", pt: "/pt/direcao-criativa/", state: "mirrored" },
  { key: "service-event-production", en: "/services/event-production/", pt: null, state: "excluded", ptFallback: "services",
    reason: "Термина нет ни разу в 3 872 PT-запросах, а соседний produção de eventos в выдаче принадлежит курсам и вакансиям. Производство живёт внутри гайда organização" },
  { key: "service-private-concierge", en: "/services/private-concierge/", pt: "/pt/concierge-privado/", state: "mirrored" },
  { key: "service-wedding-coordination", en: "/services/wedding-coordination/", pt: "/pt/coordenacao-de-casamentos/", state: "mirrored" },
  { key: "service-wedding-planning", en: "/services/wedding-planning/", pt: "/pt/organizacao-de-casamentos/", state: "mirrored" },
  { key: "terms", en: "/terms/", pt: "/pt/termos-e-condicoes/", state: "mirrored" },
  { key: "venues", en: "/venues/", pt: "/pt/quintas-para-casamentos/", state: "mirrored" },
  { key: "venue-belmond-reids-palace", en: "/venues/belmond-reids-palace/", pt: "/pt/quintas-para-casamentos/belmond-reids-palace/", state: "mirrored" },
  { key: "venue-casa-sacoto", en: "/venues/casa-sacoto/", pt: "/pt/quintas-para-casamentos/casa-sacoto/", state: "mirrored" },
  { key: "venue-palacio-da-bolsa", en: "/venues/palacio-da-bolsa/", pt: "/pt/quintas-para-casamentos/palacio-da-bolsa/", state: "mirrored" },
  { key: "venue-palacio-de-queluz", en: "/venues/palacio-de-queluz/", pt: "/pt/quintas-para-casamentos/palacio-de-queluz/", state: "mirrored" },
  { key: "venue-palacio-de-seteais", en: "/venues/palacio-de-seteais/", pt: "/pt/quintas-para-casamentos/palacio-de-seteais/", state: "mirrored" },
  { key: "venue-parques-de-sintra", en: "/venues/parques-de-sintra/", pt: "/pt/quintas-para-casamentos/parques-de-sintra/", state: "mirrored" },
  { key: "venue-penha-longa-resort", en: "/venues/penha-longa-resort/", pt: "/pt/quintas-para-casamentos/penha-longa-resort/", state: "mirrored" },
  { key: "venue-pestana-palace", en: "/venues/pestana-palace/", pt: "/pt/quintas-para-casamentos/pestana-palace/", state: "mirrored" },
  { key: "venue-quinta-da-bella-vista", en: "/venues/quinta-da-bella-vista/", pt: "/pt/quintas-para-casamentos/quinta-da-bella-vista/", state: "mirrored" },
  { key: "venue-sao-lourenco-do-barrocal", en: "/venues/sao-lourenco-do-barrocal/", pt: "/pt/quintas-para-casamentos/sao-lourenco-do-barrocal/", state: "mirrored" },
  { key: "venue-six-senses-douro-valley", en: "/venues/six-senses-douro-valley/", pt: "/pt/quintas-para-casamentos/six-senses-douro-valley/", state: "mirrored" },
  { key: "venue-the-yeatman", en: "/venues/the-yeatman/", pt: "/pt/quintas-para-casamentos/the-yeatman/", state: "mirrored" },
  { key: "venue-vidago-palace", en: "/venues/vidago-palace/", pt: "/pt/quintas-para-casamentos/vidago-palace/", state: "mirrored" },

  // PT-only: страниц нет в английской версии, потому что нет английского
  // спроса. Живут в реестре, чтобы слаг был решён и занят, но пары не
  // образуют: hreflang у них нет, переключатель не рисуется.
  { key: "pt-money-casamento", en: null, pt: "/pt/blog/quanto-custa-um-casamento-em-portugal/", state: "mirrored",
    reason: "Спецификация pt-specs-v2/03. Money-кластер: 255 запросов, 110 кликов" },
  { key: "pt-casar-no-civil", en: null, pt: "/pt/blog/quanto-custa-casar-no-civil/", state: "planned",
    reason: "Юридический кластер: 140/мес, +189% за год, ноль коммерческой конкуренции" },
  { key: "pt-como-casar", en: null, pt: "/pt/casar-em-portugal/", state: "planned",
    reason: "Процедурный гайд: 525 показов при нуле кликов у нас" },
  { key: "pt-casar-estrangeiro", en: null, pt: "/pt/casar-em-portugal-sendo-estrangeiro/", state: "planned",
    reason: "BTQ-155: роль гайда сейчас играют YouTube-влоги эмигрантов, Modo IA цитирует их за неимением текста" },
  { key: "pt-casamento-praia-algarve", en: null, pt: "/pt/casamento-na-praia-algarve/", state: "planned",
    reason: "BTQ-155: единственный кластер, где venue-интент пересекается с AIO; Algarve — гео №1 по кликам" },
  { key: "pt-orcamento-calculadora", en: null, pt: "/pt/calculadora-de-orcamento-casamento/", state: "planned",
    reason: "BTQ-155: simula.pt и VenuesIn обоих ИИ называет по имени, у нас формата нет" },
  { key: "pt-wedding-planner", en: null, pt: "/pt/wedding-planner/", state: "planned",
    reason: "pt-keyword-map §1: коммерческий интент, отдельный от информационного organizacao-de-casamentos" },
  // Исключения: двойника не будет, и это решение. Причина обязательна —
  // проверка полноты на билде отклоняет пустую.
  { key: "portal", en: "/portal/", pt: null, state: "excluded",
    reason: "Кабинет клиента — закрытая зона, не индексируется и не переводится" },
  { key: "portal-manager", en: "/portal/manager/", pt: null, state: "excluded",
    reason: "Внутренний интерфейс менеджера" },
  { key: "preview-lp-dw", en: "/preview/lp/destination-weddings-portugal", pt: null, state: "excluded",
    reason: "Превью лендинга под платный трафик (BTQ-146), noindex" },
];

const BY_KEY = new Map(ROUTES.map((r) => [r.key, r]));
const BY_EN = new Map(ROUTES.filter((r) => r.en).map((r) => [r.en as string, r]));
const BY_PT = new Map(ROUTES.filter((r) => r.pt).map((r) => [r.pt as string, r]));

/** Маршруты, у которых португальский двойник действительно существует. */
export const MIRRORED: RouteEntry[] = ROUTES.filter((r) => r.state === "mirrored");
/** Пары объявлены, страниц ещё нет. Роутов не получают. */
export const PLANNED: RouteEntry[] = ROUTES.filter((r) => r.state === "planned");
/** Двойник не нужен — с причиной. */
export const EXCLUDED: RouteEntry[] = ROUTES.filter((r) => r.state === "excluded");

/**
 * Пары, которым роутер может выдать собственный статический маршрут:
 * существующие и не сидящие на маршруте с параметром.
 */
export const STATIC_MIRRORED: RouteEntry[] = MIRRORED.filter((r) => !r.dynamic);

/** Живые португальские адреса — для prerender, карты сайта и манифеста. */
export const PT_PATHS_LIVE: string[] = MIRRORED.map((r) => r.pt as string);

/** Отрезает query и hash и приводит путь к канонической форме сайта. */
export function normalisePath(path: string): string {
  let p = (path || "/").split("?")[0].split("#")[0].toLowerCase();
  if (!p.startsWith("/")) p = "/" + p;
  if (!p.endsWith("/")) p += "/";
  return p;
}

/** Португальский раздел определяется префиксом пути, и ничем больше. */
export function localeOf(path: string): Locale {
  const p = normalisePath(path);
  return p === PT_PREFIX || p === PT_PREFIX + "/" || p.startsWith(PT_PREFIX + "/") ? "pt" : "en";
}

export function entryByKey(key: string): RouteEntry | undefined {
  return BY_KEY.get(key);
}

/** Запись, которой принадлежит путь, на любом из двух языков. */
export function entryForPath(path: string): RouteEntry | undefined {
  const p = normalisePath(path);
  return BY_EN.get(p) || BY_PT.get(p);
}

/**
 * Двойник текущей страницы на другом языке — для переключателя.
 *
 * Возвращает null, когда двойника нет или он ещё не существует: переключатель
 * в этом случае не рисуется вовсе. Уводить посетителя с его страницы на чужую
 * главную — та же потеря, что и 404.
 *
 * Возвращается КАНОНИЧЕСКАЯ форма адреса (по слэшу — своя у каждого сайта),
 * чтобы переключатель не приводил на URL, который тут же редиректит: hreflang
 * на редиректящий адрес — ошибка валидации в GSC.
 */
export function twinFor(path: string, target: Locale): string | null {
  const entry = entryForPath(path);
  if (!entry) return null;
  if (target === "pt") return entry.state === "mirrored" ? (entry.pt as string) : null;
  return entry.en;  // null у PT-only — переключатель тогда не рисуется
}

/**
 * Адрес пункта навигации на языке текущей страницы.
 *
 * Пока страницы на португальском нет, пункт ведёт на английскую версию и
 * помечается fellBack — меню остаётся полным, посетитель не упирается в 404 и
 * не попадает на полупереведённую страницу.
 */
export function localise(key: string, locale: Locale): { href: string | null; fellBack: boolean; keepsEnglish?: boolean } {
  const entry = BY_KEY.get(key);
  if (!entry) {
    if (import.meta.env?.DEV) console.warn(`[i18n] неизвестный ключ маршрута: ${key}`);
    return { href: "/", fellBack: false };
  }
  if (locale === "pt" && entry.state === "mirrored") return { href: entry.pt as string, fellBack: false };
  // PT-only без готовой страницы вести некуда — английского двойника у неё нет.
  if (!entry.en) return { href: entry.pt as string, fellBack: false };
  if (locale === "pt") {
    // Объявленное исключение: страница живёт только по-английски.
    if (entry.ptKeepEnglish) return { href: entry.en, fellBack: true, keepsEnglish: true };
    // 🔴 Иначе никогда не уводить португальского посетителя на английский адрес.
    // Либо ближайшая живая португальская страница, либо ничего.
    const sub = entry.ptFallback ? BY_KEY.get(entry.ptFallback) : undefined;
    if (sub && sub.state === "mirrored" && sub.pt) return { href: sub.pt, fellBack: true };
    return { href: null, fellBack: true };
  }
  return { href: entry.en, fellBack: false };
}

/** Короткая форма для мест, где флаг отката не нужен. null — вести некуда. */
export function href(key: string, locale: Locale): string | null {
  return localise(key, locale).href;
}

/**
 * Адрес по АНГЛИЙСКОМУ пути, переведённый в текущую локаль.
 *
 * Ссылки внутри текста страниц зашиты английскими путями — их 604 в 44 файлах,
 * и до этой функции каждая из них на португальской странице выбрасывала
 * посетителя на английский сайт. Реестр уже знает соответствие по полю `en`,
 * поэтому размечать ссылки ключами вручную не нужно: достаточно спросить
 * по пути.
 *
 * Возвращает null, когда вести некуда: своей португальской страницы нет и
 * замены не объявлено. Вызывающий рисует тогда текст без ссылки — посетитель
 * остаётся в португальской версии.
 *
 * На английской локали возвращает путь КАК ЕСТЬ, без поиска по реестру:
 * английский вывод не должен зависеть от этой функции ни одним байтом.
 */
export function localisePath(enPath: string, locale: Locale): string | null {
  if (locale === "en") return enPath;
  const entry = BY_EN.get(normalisePath(enPath));
  // Не наш маршрут — внешняя ссылка, кабинет, якорь. Оставляем как есть.
  if (!entry) return enPath;
  return localise(entry.key, locale).href;
}

export const HTML_LANG: Record<Locale, string> = { en: "en", pt: "pt-PT" };
